import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Save, Users, UserCircle, Clock, FileArchive, Loader } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { useApp } from '../context/AppContext';
import CSVExportButton from '../components/CSVExportButton';
import WorkScheduleManager from '../components/hr/WorkScheduleManager';

const COLORS = ['#C41E3A', '#10B981', '#3B82F6', '#F59E0B', '#8B5CF6'];

const formatRM = (value) => `RM${(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

const StaffForm = ({ onSubmit, onClose, initialData }) => {
  const [formData, setFormData] = useState(initialData || {
    name: '',
    ic_number: '',
    basic_salary: '',
    reduction_percent: 0,
    allowance: 0,
    overtime_hours: 0,
    overtime_rate: 0,
    pcb_amount: 0,
    status: 'Active',
    department: '',
    position: '',
    bank_name: '',
    bank_account: '',
    phone: '',
    email: '',
    address: '',
    join_date: ''
  });

  // Calculate preview
  const basic = parseFloat(formData.basic_salary) || 0;
  const reductionPercent = parseFloat(formData.reduction_percent) || 0;
  const allowance = parseFloat(formData.allowance) || 0;
  const otHours = parseFloat(formData.overtime_hours) || 0;
  const otRate = parseFloat(formData.overtime_rate) || (basic / 26 / 8 * 1.5);
  const pcb = parseFloat(formData.pcb_amount) || 0;
  
  const reductionAmount = basic * (reductionPercent / 100);
  const newBasic = basic - reductionAmount;
  const overtimePay = otHours * otRate;
  const grossSalary = newBasic + allowance + overtimePay;
  const epfEmployee = grossSalary * 0.11;
  const socsoEis = grossSalary * 0.004;
  const totalDeductions = epfEmployee + socsoEis + pcb;
  const netPay = grossSalary - totalDeductions;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      basic_salary: parseFloat(formData.basic_salary),
      reduction_percent: parseFloat(formData.reduction_percent) || 0,
      allowance: parseFloat(formData.allowance) || 0,
      overtime_hours: parseFloat(formData.overtime_hours) || 0,
      overtime_rate: parseFloat(formData.overtime_rate) || 0,
      pcb_amount: parseFloat(formData.pcb_amount) || 0
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Personal Info */}
      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
        <p className="text-xs font-semibold text-gray-600 mb-3 flex items-center gap-1">
          <Users className="w-3 h-3" /> Personal Information
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Staff Name *</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" required placeholder="Full name as per IC" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">IC Number</label>
            <input type="text" value={formData.ic_number} onChange={(e) => setFormData({...formData, ic_number: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" placeholder="e.g. 901234-12-5678" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Join Date</label>
            <input type="date" value={formData.join_date} onChange={(e) => setFormData({...formData, join_date: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Department</label>
            <input type="text" value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" placeholder="e.g. Sales" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Position</label>
            <input type="text" value={formData.position} onChange={(e) => setFormData({...formData, position: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" placeholder="e.g. Executive" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm">
              <option value="Active">Active</option>
              <option value="Resigned">Resigned</option>
              <option value="On Leave">On Leave</option>
            </select>
          </div>
        </div>
      </div>

      {/* Contact Info */}
      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
        <p className="text-xs font-semibold text-gray-600 mb-3">Contact Details</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
            <input type="text" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" placeholder="e.g. 012-3456789" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" placeholder="email@company.com" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
            <textarea value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" rows={2} placeholder="Full address" />
          </div>
        </div>
      </div>

      {/* Bank Details */}
      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
        <p className="text-xs font-semibold text-gray-600 mb-3">Bank Details (for salary)</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Bank Name</label>
            <input type="text" value={formData.bank_name} onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" placeholder="e.g. Maybank" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Account Number</label>
            <input type="text" value={formData.bank_account} onChange={(e) => setFormData({...formData, bank_account: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" placeholder="Account number" />
          </div>
        </div>
      </div>

      {/* Salary */}
      <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
        <p className="text-xs font-semibold text-blue-700 mb-3">Salary Components</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Basic Salary (RM) *</label>
            <input type="number" step="0.01" value={formData.basic_salary} onChange={(e) => setFormData({...formData, basic_salary: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" required placeholder="0.00" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Reduction (%)</label>
            <input type="number" step="0.1" value={formData.reduction_percent} onChange={(e) => setFormData({...formData, reduction_percent: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" placeholder="0" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Allowance (RM)</label>
            <input type="number" step="0.01" value={formData.allowance} onChange={(e) => setFormData({...formData, allowance: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" placeholder="0.00" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">OT Hours</label>
            <input type="number" step="0.5" value={formData.overtime_hours} onChange={(e) => setFormData({...formData, overtime_hours: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" placeholder="0" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">OT Rate (RM/hr)</label>
            <input type="number" step="0.01" value={formData.overtime_rate} onChange={(e) => setFormData({...formData, overtime_rate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" placeholder={`Auto: ${otRate.toFixed(2)}`} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">PCB (Tax RM)</label>
            <input type="number" step="0.01" value={formData.pcb_amount} onChange={(e) => setFormData({...formData, pcb_amount: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" placeholder="0.00" />
          </div>
        </div>
      </div>

      {/* Preview */}
      {basic > 0 && (
        <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
          <p className="text-xs font-semibold text-emerald-700 mb-2">Salary Preview</p>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="bg-white rounded p-2">
              <span className="text-gray-500 block">Gross</span>
              <span className="font-bold text-gray-800">{formatRM(grossSalary)}</span>
            </div>
            <div className="bg-white rounded p-2">
              <span className="text-gray-500 block">Deductions</span>
              <span className="font-bold text-red-600">-{formatRM(totalDeductions)}</span>
            </div>
            <div className="bg-white rounded p-2">
              <span className="text-gray-500 block">Net Pay</span>
              <span className="font-bold text-emerald-600">{formatRM(netPay)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">Cancel</button>
        <button type="submit" className="flex-1 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center justify-center gap-2 text-sm">
          <Save className="w-4 h-4" /> Save Staff
        </button>
      </div>
    </form>
  );
};

const StaffPayrollPage = () => {
  const { staffList, payrollSummary, addStaff, updateStaff, deleteStaff, staffLoading } = useApp();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);

  const handleAdd = async (data) => {
    await addStaff(data);
    setModalOpen(false);
  };

  const handleEdit = async (data) => {
    await updateStaff(editingItem.id, data);
    setEditingItem(null);
    setModalOpen(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this staff member? This action cannot be undone.')) {
      await deleteStaff(id);
    }
  };

  const handleSyncPayroll = async () => {
    const nowObj = new Date();
    const defaultMonth = `${nowObj.getFullYear()}-${String(nowObj.getMonth() + 1).padStart(2, '0')}`;
    const month = window.prompt(
      'Sync gaji bulan mana ke Expenses? (Format: YYYY-MM)\n\nAuto-posting akan create/update entry "Staff Cost" dalam Expenses page.',
      defaultMonth
    );
    if (!month || !/^\d{4}-\d{2}$/.test(month)) return;
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiUrl}/api/staff/payroll/sync-to-expenses?month=${month}`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Sync gagal');
      alert(
        `✅ Payroll ${month} berjaya sync ke Expenses:\n` +
        `- Created: ${data.created}\n- Updated: ${data.updated}\n` +
        `- Total: RM ${(data.total_amount || 0).toLocaleString()}\n` +
        `- Staff: ${data.active_staff_count}`
      );
    } catch (err) {
      alert('Ralat: ' + err.message);
    }
  };

  const [bulkPayrollBusy, setBulkPayrollBusy] = useState(false);
  const handleBulkPayrollZip = async () => {
    const nowObj = new Date();
    const defaultMonth = `${nowObj.getFullYear()}-${String(nowObj.getMonth() + 1).padStart(2, '0')}`;
    const month = window.prompt(
      'Jana semua payslip PDF untuk bulan mana? (Format: YYYY-MM)\n\nSistem akan gabung dalam 1 fail ZIP untuk muat turun sekali gus.',
      defaultMonth
    );
    if (!month || !/^\d{4}-\d{2}$/.test(month)) return;
    setBulkPayrollBusy(true);
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiUrl}/api/payroll/bulk/pdf-zip?month=${month}`, {
        method: 'GET', credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const generated = res.headers.get('X-Payslips-Generated') || '?';
      const failed = res.headers.get('X-Payslips-Failed') || '0';
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `UA_Syarikat_Payroll_${month}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      alert(`✅ ${generated} payslip PDF siap dalam ZIP. ${failed !== '0' ? `⚠️ ${failed} gagal (check _SUMMARY.txt dalam ZIP).` : ''}`);
    } catch (err) {
      alert('Ralat bulk payroll: ' + err.message);
    } finally {
      setBulkPayrollBusy(false);
    }
  };

  // Calculate totals
  const activeStaff = staffList.filter(s => s.status === 'Active');
  const totalGross = staffList.reduce((sum, s) => sum + (s.gross_salary || 0), 0);
  const totalNet = staffList.reduce((sum, s) => sum + (s.net_pay || 0), 0);
  const totalEPF = staffList.reduce((sum, s) => sum + (s.epf_employee || 0), 0);

  // Department breakdown
  const deptData = {};
  staffList.forEach(s => {
    const dept = s.department || 'Unassigned';
    if (!deptData[dept]) deptData[dept] = { name: dept, count: 0, total: 0 };
    deptData[dept].count++;
    deptData[dept].total += s.net_pay || 0;
  });
  const departmentData = Object.values(deptData);

  if (staffLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Staff & Payroll</h1>
          <p className="text-gray-500 text-xs sm:text-sm">Manage employees and payroll calculations</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setScheduleModalOpen(true)}
            data-testid="set-schedule-btn"
            title="Tetapkan waktu bekerja staff (per individu atau bulk per department)"
            className="px-3 sm:px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2 text-sm whitespace-nowrap">
            <Clock className="w-4 h-4" /> Waktu Kerja
          </button>
          <button onClick={handleSyncPayroll}
            data-testid="sync-payroll-btn"
            title="Auto-post gaji bulan ke Expenses (avoid double entry)"
            className="px-3 sm:px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2 text-sm whitespace-nowrap">
            <Save className="w-4 h-4" /> Sync ke Expenses
          </button>
          <button onClick={handleBulkPayrollZip}
            data-testid="bulk-payroll-zip-btn"
            disabled={bulkPayrollBusy}
            title="Jana 1-klik semua payslip PDF untuk bulan terpilih dalam satu fail ZIP"
            className="px-3 sm:px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2 text-sm whitespace-nowrap">
            {bulkPayrollBusy ? <Loader className="w-4 h-4 animate-spin" /> : <FileArchive className="w-4 h-4" />}
            {bulkPayrollBusy ? 'Menjana...' : 'Proses Semua (ZIP)'}
          </button>
          <CSVExportButton endpoint="/export/payroll.csv" filename="payroll" testId="export-payroll-csv" />
          <button onClick={() => { setEditingItem(null); setModalOpen(true); }}
            data-testid="add-staff-btn"
            className="px-3 sm:px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center gap-2 text-sm whitespace-nowrap">
            <Plus className="w-4 h-4" /> Add Staff
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-100 shadow-sm border-l-4 border-l-blue-500 min-w-0">
          <p className="text-gray-500 text-xs sm:text-sm mb-1">Total Staff</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-800">{staffList.length}</p>
          <p className="text-xs text-blue-600">{activeStaff.length} active</p>
        </div>
        <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-100 shadow-sm border-l-4 border-l-emerald-500 min-w-0">
          <p className="text-gray-500 text-xs sm:text-sm mb-1">Total Gross Salary</p>
          <p className="text-lg sm:text-2xl font-bold text-emerald-600 truncate">{formatRM(totalGross)}</p>
        </div>
        <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-100 shadow-sm border-l-4 border-l-red-500 min-w-0">
          <p className="text-gray-500 text-xs sm:text-sm mb-1">Total EPF (Employee)</p>
          <p className="text-lg sm:text-2xl font-bold text-red-600 truncate">{formatRM(totalEPF)}</p>
        </div>
        <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-100 shadow-sm border-l-4 border-l-amber-500 min-w-0">
          <p className="text-gray-500 text-xs sm:text-sm mb-1">Total Net Pay</p>
          <p className="text-lg sm:text-2xl font-bold text-amber-600 truncate">{formatRM(totalNet)}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Department Breakdown */}
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 shadow-sm overflow-hidden min-w-0">
          <h3 className="text-gray-800 font-medium mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-red-700" /> By Department
          </h3>
          <div style={{ width: '100%', height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={departmentData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="count" nameKey="name">
                  {departmentData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [`${value} staff`, name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap justify-center gap-2 mt-2">
            {departmentData.map((item, index) => (
              <div key={item.name} className="flex items-center gap-1 text-xs">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                <span className="text-gray-600">{item.name} ({item.count})</span>
              </div>
            ))}
          </div>
        </div>

        {/* Salary by Department */}
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 shadow-sm overflow-hidden min-w-0">
          <h3 className="text-gray-800 font-medium mb-4">Salary by Department</h3>
          <div style={{ width: '100%', height: 250 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={280}>
              <BarChart data={departmentData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 10 }} />
                <YAxis dataKey="name" type="category" tick={{ fill: '#6b7280', fontSize: 10 }} width={70} />
                <Tooltip formatter={(value) => formatRM(value)} />
                <Bar dataKey="total" fill="#C41E3A" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Staff Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-red-700 to-red-800 flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-semibold text-white">Staff List</h3>
          <p className="text-xs text-red-100 flex items-center gap-1.5">
            <UserCircle className="w-4 h-4" />
            Klik nama / baris staff untuk buka <b>Profile 360°</b> (lihat semua info staff dalam satu halaman)
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3 text-gray-600 font-medium">Name</th>
                <th className="text-left p-3 text-gray-600 font-medium">Department</th>
                <th className="text-left p-3 text-gray-600 font-medium">Position</th>
                <th className="text-right p-3 text-gray-600 font-medium">Basic</th>
                <th className="text-right p-3 text-gray-600 font-medium">Gross</th>
                <th className="text-right p-3 text-gray-600 font-medium">Net Pay</th>
                <th className="text-center p-3 text-gray-600 font-medium">Status</th>
                <th className="text-center p-3 text-gray-600 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {staffList.map((staff) => (
                <tr
                  key={staff.id}
                  className="hover:bg-red-50/40 cursor-pointer transition-colors"
                  onClick={() => navigate(`/staff/${staff.id}`)}
                  data-testid={`staff-row-${staff.id}`}
                >
                  <td className="p-3">
                    <p className="font-medium text-gray-800 hover:text-red-700">{staff.name}</p>
                    <p className="text-xs text-gray-500">{staff.ic_number}</p>
                  </td>
                  <td className="p-3 text-gray-600">{staff.department || '-'}</td>
                  <td className="p-3 text-gray-600">{staff.position || '-'}</td>
                  <td className="p-3 text-right text-gray-700">{formatRM(staff.basic_salary)}</td>
                  <td className="p-3 text-right text-gray-700">{formatRM(staff.gross_salary)}</td>
                  <td className="p-3 text-right font-medium text-emerald-600">{formatRM(staff.net_pay)}</td>
                  <td className="p-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      staff.status === 'Active' ? 'bg-emerald-100 text-emerald-700' :
                      staff.status === 'Resigned' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {staff.status}
                    </span>
                  </td>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => navigate(`/staff/${staff.id}`)}
                        data-testid={`staff-view360-${staff.id}`}
                        title="Lihat Profil 360°"
                        className="p-2 hover:bg-red-50 rounded text-red-600 hover:text-red-700">
                        <UserCircle className="w-4 h-4" />
                      </button>
                      <button onClick={() => { setEditingItem(staff); setModalOpen(true); }}
                        data-testid={`staff-edit-${staff.id}`}
                        className="p-2 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-700">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(staff.id)}
                        data-testid={`staff-delete-${staff.id}`}
                        className="p-2 hover:bg-red-50 rounded text-gray-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {staffList.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-gray-500">No staff records yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={modalOpen} onClose={() => { setModalOpen(false); setEditingItem(null); }} title={editingItem ? 'Edit Staff' : 'Add New Staff'}>
        <StaffForm onSubmit={editingItem ? handleEdit : handleAdd} onClose={() => { setModalOpen(false); setEditingItem(null); }} initialData={editingItem} />
      </Modal>

      <WorkScheduleManager
        isOpen={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        staffList={staffList}
      />
    </div>
  );
};

export default StaffPayrollPage;
